import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { pb } from '../lib/pocketbase.js';
import { useAuth } from '../auth/AuthContext.jsx';
import PartnerFormModal from '../components/PartnerFormModal.jsx';
import AnsprechpartnerFormModal from '../components/AnsprechpartnerFormModal.jsx';
import {
  PARTNER_TYP_BADGE,
  PARTNER_TYP_LABEL,
} from '../lib/partner.js';
import { formatWebsiteHref } from '../lib/format.js';

function TypPill({ typ }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
        PARTNER_TYP_BADGE[typ] ?? 'bg-slate-100 text-slate-700 ring-slate-200'
      }`}
    >
      {PARTNER_TYP_LABEL[typ] ?? typ}
    </span>
  );
}

function Feldzeile({ label, children }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="mt-0.5 text-sm text-slate-800">
        {children || <span className="text-slate-400">—</span>}
      </div>
    </div>
  );
}

// Card mit den Ansprechpartnern eines Partners. Sensible Daten — wird von
// PartnerDetailPage nur gerendert, wenn der User Admin/Führungskraft ist
// (Defense-in-depth zusätzlich zur FuehrungskraftRoute und den PB-Rules).
// Die Card lädt ihre Liste erst beim Rendern, d. h. nie für Vertriebs-User.
function AnsprechpartnerCard({ partnerId }) {
  const [liste, setListe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [offeneNotizen, setOffeneNotizen] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await pb.collection('partner_ansprechpartner').getFullList({
        filter: pb.filter('partner = {:id}', { id: partnerId }),
        sort: 'name',
      });
      setListe(items);
    } catch (err) {
      setError(err?.message || 'Ansprechpartner konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    load();
  }, [load]);

  function openCreate() {
    setEditTarget(null);
    setModalOpen(true);
  }

  function openEdit(a) {
    setEditTarget(a);
    setModalOpen(true);
  }

  function toggleNotiz(id) {
    setOffeneNotizen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleDelete(a) {
    if (!window.confirm(`Ansprechpartner "${a.name}" wirklich löschen?`)) return;
    try {
      await pb.collection('partner_ansprechpartner').delete(a.id);
      setListe((prev) => prev.filter((x) => x.id !== a.id));
    } catch (err) {
      alert(err?.message || 'Löschen fehlgeschlagen.');
    }
  }

  return (
    <div className="card space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">Ansprechpartner</h2>
        <button type="button" className="btn-secondary" onClick={openCreate}>
          + Ansprechpartner hinzufügen
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Lade …</div>
      ) : liste.length === 0 ? (
        <div className="text-sm text-slate-400">Noch keine Ansprechpartner hinterlegt.</div>
      ) : (
        <ul className="divide-y divide-slate-200">
          {liste.map((a) => {
            const notizOffen = offeneNotizen.has(a.id);
            return (
              <li key={a.id} className="py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900">{a.name}</span>
                      {a.rolle && (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-200">
                          {a.rolle}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                      {a.email && (
                        <a
                          href={`mailto:${a.email}`}
                          className="text-primary-700 hover:underline"
                        >
                          {a.email}
                        </a>
                      )}
                      {a.telefon && (
                        <a href={`tel:${a.telefon}`} className="text-primary-700 hover:underline">
                          {a.telefon}
                        </a>
                      )}
                    </div>
                    {a.notiz && (
                      <div className="mt-1">
                        <button
                          type="button"
                          className="text-xs font-medium text-slate-500 hover:text-slate-700"
                          onClick={() => toggleNotiz(a.id)}
                        >
                          {notizOffen ? '▾ Notiz ausblenden' : '▸ Notiz anzeigen'}
                        </button>
                        {notizOffen && (
                          <div className="mt-1 whitespace-pre-wrap rounded-md bg-slate-50 p-2 text-sm text-slate-700 ring-1 ring-inset ring-slate-200">
                            {a.notiz}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-3">
                    <button
                      type="button"
                      className="text-sm font-medium text-primary-600 hover:text-primary-800"
                      onClick={() => openEdit(a)}
                    >
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      className="text-sm font-medium text-red-600 hover:text-red-800"
                      onClick={() => handleDelete(a)}
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <AnsprechpartnerFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        partnerId={partnerId}
        ansprechpartner={editTarget}
        onSaved={() => {
          setModalOpen(false);
          load();
        }}
      />
    </div>
  );
}

export default function PartnerDetailPage() {
  const { id } = useParams();
  const { isFuehrungskraftOrAdmin } = useAuth();
  const [partner, setPartner] = useState(null);
  const [praxenCount, setPraxenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [record, count] = await Promise.all([
        pb.collection('partner').getOne(id),
        pb
          .collection('praxen')
          .getList(1, 1, { filter: pb.filter('partner = {:id}', { id }) })
          .then((r) => r.totalItems),
      ]);
      setPartner(record);
      setPraxenCount(count);
    } catch (err) {
      setError(err?.message || 'Partner konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className="text-sm text-slate-500">Lade …</div>;
  }

  if (error || !partner) {
    return (
      <div className="space-y-4">
        <Link to="/partner" className="text-sm text-primary-700 hover:underline">
          ← zurück zur Partner-Liste
        </Link>
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
          {error || 'Partner nicht gefunden.'}
        </div>
      </div>
    );
  }

  const websiteHref = formatWebsiteHref(partner.website);

  return (
    <div className="space-y-6">
      <Link to="/partner" className="text-sm text-primary-700 hover:underline">
        ← zurück zur Partner-Liste
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">{partner.name}</h1>
            <TypPill typ={partner.typ} />
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {praxenCount > 0 ? (
              <Link
                to={`/praxen?partner=${partner.id}`}
                className="font-medium text-primary-700 hover:underline"
              >
                {praxenCount} zugeordnete Praxis{praxenCount === 1 ? '' : 'en'}
              </Link>
            ) : (
              'Keine Praxen als Empfehler zugeordnet'
            )}
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => setEditOpen(true)}>
          Bearbeiten
        </button>
      </div>

      {/* Stammdaten */}
      <div className="card space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Stammdaten</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Feldzeile label="Kontaktperson">{partner.kontaktperson}</Feldzeile>
          <Feldzeile label="Telefon">
            {partner.telefon ? (
              <a href={`tel:${partner.telefon}`} className="text-primary-700 hover:underline">
                {partner.telefon}
              </a>
            ) : null}
          </Feldzeile>
          <Feldzeile label="E-Mail">
            {partner.email ? (
              <a href={`mailto:${partner.email}`} className="text-primary-700 hover:underline">
                {partner.email}
              </a>
            ) : null}
          </Feldzeile>
          <Feldzeile label="Website">
            {websiteHref ? (
              <a
                href={websiteHref}
                target="_blank"
                rel="noreferrer"
                className="text-primary-700 hover:underline"
              >
                {partner.website}
              </a>
            ) : null}
          </Feldzeile>
        </div>
        {partner.notizen && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Notizen
            </div>
            <div
              className="prose prose-sm mt-1 max-w-none text-slate-800"
              dangerouslySetInnerHTML={{ __html: partner.notizen }}
            />
          </div>
        )}
      </div>

      {/* Adresse */}
      <div className="card space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Adresse</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Feldzeile label="Straße">{partner.partner_strasse}</Feldzeile>
          <Feldzeile label="PLZ / Ort">
            {[partner.partner_plz, partner.partner_ort].filter(Boolean).join(' ')}
          </Feldzeile>
          <Feldzeile label="Bundesland">{partner.partner_bundesland}</Feldzeile>
          <Feldzeile label="Land">{partner.partner_land}</Feldzeile>
        </div>
      </div>

      {/* Ansprechpartner — sensible Daten, nur für Admin/Führungskraft.
          Card wird komplett weggelassen (nicht ausgegraut), konsistent
          mit dem übrigen CRM. Backend zusätzlich per PB-Rules geschützt. */}
      {isFuehrungskraftOrAdmin && <AnsprechpartnerCard partnerId={partner.id} />}

      <PartnerFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        partner={partner}
        onSaved={() => {
          setEditOpen(false);
          load();
        }}
      />
    </div>
  );
}
